/**
 * Which plan they upgraded to — asked, not assumed.
 *
 * "Plan upgradation" closed the chase and recorded the word, and the word on
 * its own is the least useful half of the answer: the whole reason anybody
 * runs a trial follow-up is to find out whether GROWTH or SUITE was the one,
 * and a month later the report can only say that twenty-one people upgraded
 * to something.
 *
 * So the plan is picked here, priced from the same list the rest of the app
 * bills from, and written onto the account as well as into the round. The
 * cycle comes too, because 199 a month and 199 a year are not the same sale
 * and the ledger has to know which.
 *
 * The current plan is shown and cannot be picked. An "upgrade" to the plan
 * they are already on is somebody misreading the dialog, and the one thing
 * this window must not do is quietly file that as a win.
 */
import { useState } from 'react';
import { useApp } from '../../store/AppContext.jsx';
import Icon from '../../lib/icons.jsx';
import Sheet, { Group, Field } from '../Sheet.jsx';

/*
 * The two ways XYRA is actually sold.
 *
 * `S.cycles` carries six — Quarterly, Half-Yearly and Lifetime among them —
 * because it is the list the whole ledger has to be able to *read*, including
 * accounts imported on terms nobody offers any more. It is not the list of
 * terms somebody can be sold today, and offering four dead options on the one
 * screen where a sale is being recorded is how a Lifetime account gets
 * created by a mis-click.
 */
const CYCLES = ['Monthly', 'Annual'];

export default function PlanUpgradeSheet({ id, onConfirm, onClose }) {
  const { S, rows, fmtMoney } = useApp();
  const client = (rows || []).find(r => String(r.id) === String(id));

  const plans = S.plans || [];
  const now = String((client && client.plan) || '').trim().toUpperCase();
  /* Where they are on the ladder, so everything above it is an upgrade and
     everything at or below it is not. A plan the list does not carry — an old
     name, or a blank — puts them at the bottom, where every plan is offered. */
  const at = plans.findIndex(p => String(p).toUpperCase() === now);

  const [plan, setPlan] = useState(() => plans.find((p, i) => i > at) || '');
  /* Whatever they are on now, where it is still a term we sell; Monthly
     otherwise, which is what a mis-set old cycle should not carry forward. */
  const [cycle, setCycle] = useState(
    () => (CYCLES.includes((client && client.cycle) || '') ? client.cycle : 'Monthly'));
  const [note, setNote] = useState('');

  if (!client) return null;

  const priceOf = p => Number((S.prices || {})[String(p).toUpperCase()] || 0);

  return (
    <Sheet
      icon="up"
      tone="g-green"
      title="Plan upgradation"
      onClose={onClose}
      onSave={plan ? () => onConfirm({ plan, cycle, note: note.trim() }) : undefined}
      saveLabel="Upgrade and close"
      bodyClass="pu-form"
      hero={(
        <div className="pu-hero">
          <b>{client.name || 'This account'}</b>
          <span>
            {now ? `On ${now} today` : 'No plan recorded today'}
            {client.cycle ? ` · billed ${String(client.cycle).toLowerCase()}` : ''}
          </span>
        </div>
      )}
    >
      <Group>Moving to</Group>

      <div className="pu-plans">
        {plans.map(p => {
          const isNow = String(p).toUpperCase() === now;
          const price = priceOf(p);
          return (
            <button
              key={p}
              type="button"
              disabled={isNow}
              className={`pu-plan${plan === p ? ' on' : ''}${isNow ? ' now' : ''}`}
              onClick={() => setPlan(p)}
            >
              <b>{p}</b>
              <span>{price > 0 ? `${S.currency} ${price}/mo` : 'Free'}</span>
              {isNow ? <em>on this today</em> : plan === p ? <Icon name="check" /> : null}
            </button>
          );
        })}
      </div>

      <Field label="Billed">
        <select value={cycle} onChange={e => setCycle(e.target.value)}>
          {CYCLES.map(c => <option key={c} value={c}>{c}</option>)}
        </select>
      </Field>

      <Field label="What they said" wide>
        <input
          value={note}
          onChange={e => setNote(e.target.value)}
          placeholder="Wanted the extra users. Paying by card on Thursday."
        />
      </Field>

      {plan ? (
        <p className="pu-sum">
          <Icon name="check" />
          {`${client.name} moves to ${plan}, billed ${String(cycle).toLowerCase()}`}
          {priceOf(plan) > 0 ? ` — ${fmtMoney(priceOf(plan))} a month` : ''}
          {'. The follow-up closes.'}
        </p>
      ) : (
        <p className="pu-sum none">
          <Icon name="alert" />
          Pick the plan they moved to. Recording the upgrade without it leaves
          the report able to say only that somebody upgraded to something.
        </p>
      )}
    </Sheet>
  );
}
